import type {
  NpcPlayback,
  NpcPlaybackSpeed,
  NpcPlaybackStep,
  SessionValues,
  SessionView,
} from '@matter_extractor/shared';

/**
 * The PLAYBACK of a finished replay (npc.md §11, H17, issue #210), PURE.
 *
 * The server hands the recorded sequence - what the person saw, in the order it happened, each step
 * with its offset from the start - and the screen the replay ENDS on. The player re-draws the
 * screen at any instant of that sequence: this module says which page stood, which temps had
 * opened, which bubbles were written and which values were given at that instant. Nothing is
 * computed about the replay itself (N10): it replays, it does not measure.
 *
 * ## Playback time is not wall time
 *
 * « On n'attend pas l'interviewer devant le client » (§11): the recorded offsets hold every second
 * a model took to answer, which a demo does not want to sit through. The sequence is laid on a
 * PLAYBACK clock where no gap between two steps lasts more than `PLAYBACK_GAP_CAP_MS` - the order
 * and the rhythm of a quick exchange stay, the long silences go. The speed (×1, ×2, ×4) then scales
 * that clock.
 */

/** The longest a silence between two recorded steps lasts on the playback clock. */
export const PLAYBACK_GAP_CAP_MS = 3000;
/** What the clock keeps running after the last step, so the last page is seen before the end. */
export const PLAYBACK_TAIL_MS = 1500;

/** One recorded step laid on the playback clock. */
export interface TimedStep {
  readonly step: NpcPlaybackStep;
  /** Milliseconds on the playback clock, from 0. */
  readonly at: number;
}

/** The recorded steps laid on the playback clock, in order, no silence longer than the cap. */
export function timedSteps(steps: readonly NpcPlaybackStep[]): readonly TimedStep[] {
  const timed: TimedStep[] = [];
  let clock = 0;
  let previous = steps[0]?.offsetMs ?? 0;
  for (const step of steps) {
    clock += Math.min(PLAYBACK_GAP_CAP_MS, Math.max(0, step.offsetMs - previous));
    previous = step.offsetMs;
    timed.push({ step, at: clock });
  }
  return timed;
}

/** How long the playback lasts on its clock. */
export function playbackLength(timed: readonly TimedStep[]): number {
  const last = timed[timed.length - 1];
  return last === undefined ? 0 : last.at + PLAYBACK_TAIL_MS;
}

/** One mark of the line of progress: a page entered. */
export interface PageMark {
  readonly pageId: string;
  readonly name: string;
  readonly at: number;
  /** 1-based rank of the page among the pages the replay walked. */
  readonly rank: number;
}

/** The marks of the line of progress, one per page entered (§11). */
export function pageMarks(timed: readonly TimedStep[]): readonly PageMark[] {
  const marks: PageMark[] = [];
  for (const { step, at } of timed) {
    if (step.type !== 'page') continue;
    marks.push({ pageId: step.pageId, name: step.name, at, rank: marks.length + 1 });
  }
  return marks;
}

/** « Page suivante »: the instant of the next page after `at`, or null on the last one. */
export function nextPageAt(timed: readonly TimedStep[], at: number): number | null {
  const next = pageMarks(timed).find((mark) => mark.at > at);
  return next === undefined ? null : next.at;
}

/** What the screen shows at one instant of the playback. */
export interface PlaybackFrame {
  /** The view the player draws: the ending screen, rewound to the instant. */
  readonly view: SessionView;
  /**
   * How many temps of the page on screen are open, for the reader to draw - or null when the
   * recording holds no temps for that page, and the reader plays the page's own.
   */
  readonly revealed: number | null;
  /** « page 4 / 8 ». */
  readonly page: number;
  readonly pages: number;
  /** « tour 7 / 22 »: the turns of the conversation written so far, and in all. */
  readonly turn: number;
  readonly turns: number;
  /** The recording is over: the real status of the session stands. */
  readonly over: boolean;
}

function isTurn(kind: string): boolean {
  return kind === 'turn';
}

/**
 * The screen at `at` on the playback clock: the page that stood, its temps, the bubbles written so
 * far, the values given on the pages left. The rest of the view - the experience with its theme, the
 * persona, the matter - is the ending screen's, which is all the reader has to paint a page.
 */
export function frameAt(
  playback: NpcPlayback,
  timed: readonly TimedStep[],
  at: number,
): PlaybackFrame {
  const length = playbackLength(timed);
  const over = at >= length;
  const marks = pageMarks(timed);
  const stood = [...marks].reverse().find((mark) => mark.at <= at) ?? marks[0] ?? null;

  const values: SessionValues = {};
  let revealed = 0;
  let recorded = false;
  let messages = 0;
  let turn = 0;
  for (const { step, at: stepAt } of timed) {
    if (step.type === 'temps' && stood !== null && step.pageId === stood.pageId) recorded = true;
    if (stepAt > at) continue;
    if (step.type === 'leave') Object.assign(values, step.values);
    if (step.type === 'message') {
      messages += 1;
      if (isTurn(step.kind)) turn += 1;
    }
    if (
      step.type === 'temps' &&
      stood !== null &&
      step.pageId === stood.pageId &&
      stepAt >= stood.at
    ) {
      revealed = Math.max(revealed, step.index);
    }
  }
  const turns = timed.filter(({ step }) => step.type === 'message' && isTurn(step.kind)).length;

  const conversation = (playback.screen.conversation ?? []).slice(0, messages);
  const view: SessionView = {
    ...playback.screen,
    status: over ? playback.screen.status : 'in-progress',
    endedAt: over ? playback.screen.endedAt : null,
    closeReason: over ? playback.screen.closeReason : null,
    currentPageId: stood === null ? playback.screen.currentPageId : stood.pageId,
    values: over ? { ...playback.screen.values } : values,
    conversation,
  };
  return {
    view,
    revealed: recorded ? revealed : null,
    page: stood?.rank ?? 0,
    pages: marks.length,
    turn,
    turns,
    over,
  };
}

/** The speeds the player offers, as the buttons write them: « ×1 », « ×2 », « ×4 ». */
export function speedLabel(speed: NpcPlaybackSpeed): string {
  return `×${speed}`;
}

/** The next clock value after `elapsedMs` of wall time at `speed`, clamped to the length. */
export function advanceClock(
  at: number,
  elapsedMs: number,
  speed: NpcPlaybackSpeed,
  length: number,
): number {
  return Math.min(length, at + elapsedMs * speed);
}
